import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import { useErrorHandlingSubscription } from '@backoffice-frontend/data-fetching'
import { useGlobalRegion } from '@backoffice-frontend/service-area-picker'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { createContext, useEffect, useRef, useState } from 'react'
import { useFccMostCriticalVehicleSubscription } from './AdVehicleStream.subscription.hook'

type AdVehicleStreamValue = {
  vehicleId?: string
  handledByBackofficeUserId?: string
  isHandledByCurrentUser: boolean
  pendingUpdate: boolean
  setPendingUpdate: Dispatch<SetStateAction<boolean>>
}

export const AdVehicleStreamContext = createContext<
  AdVehicleStreamValue | undefined
>(undefined)

type AdVehicleStreamProviderProps = {
  children: ReactNode
}

export const AdVehicleStreamProvider = ({
  children,
}: AdVehicleStreamProviderProps) => {
  const { user } = useBackofficeLoggedInUser()
  const [region] = useGlobalRegion()
  const { isActive: shouldSubscribeToMostCriticalVehicle } =
    useAdOperatorAvailability()
  const [vehicleId, setVehicleId] = useState<string | undefined>(undefined)
  const [pendingUpdate, setPendingUpdate] = useState<boolean>(false)

  const [handledByBackofficeUserId, setHandledByBackofficeUserId] = useState<
    string | undefined
  >(undefined)

  const isHandledByCurrentUser = Boolean(
    !!vehicleId &&
      handledByBackofficeUserId &&
      user?.id &&
      handledByBackofficeUserId === user.id,
  )

  const prevIsHandledByCurrentUserRef = useRef(isHandledByCurrentUser)
  useEffect(() => {
    if (prevIsHandledByCurrentUserRef.current !== isHandledByCurrentUser) {
      console.info('[AdVehicleStream] isHandledByCurrentUser changed', {
        isHandledByCurrentUser,
        vehicleId,
        handledByBackofficeUserId,
        userId: user?.id,
      })
      prevIsHandledByCurrentUserRef.current = isHandledByCurrentUser
    }
  }, [isHandledByCurrentUser, vehicleId, handledByBackofficeUserId, user?.id])

  const isSubscriptionSkipped =
    !user?.id || !region || !shouldSubscribeToMostCriticalVehicle
  useEffect(() => {
    if (isSubscriptionSkipped) {
      console.info('[AdVehicleStream] subscription skipped', {
        hasUserId: !!user?.id,
        hasRegion: !!region,
        shouldSubscribeToMostCriticalVehicle,
      })
    }
  }, [
    isSubscriptionSkipped,
    user?.id,
    region,
    shouldSubscribeToMostCriticalVehicle,
  ])

  useErrorHandlingSubscription(
    useFccMostCriticalVehicleSubscription,
    // Only if user is available or busy we want to subscribe to vehicle handling updates:
    user?.id && region && shouldSubscribeToMostCriticalVehicle
      ? {
          variables: {
            backofficeUserId: user.id,
            regionId: region.id,
          },
          fetchPolicy: 'no-cache',
          onError: (e: unknown) => {
            console.error('Error in most critical vehicle subscription', e)
            setVehicleId(undefined)
            setPendingUpdate(false)
          },
          onData: ({ data }) => {
            if (data.data?.fccMostCriticalVehicle) {
              const receivedVehicleId =
                data.data.fccMostCriticalVehicle.vehicle?.id ?? undefined
              const receivedHandledBy =
                data.data.fccMostCriticalVehicle.vehicle
                  ?.handledByBackofficeUserId ?? undefined

              console.info('[AdVehicleStream] subscription onData', {
                vehicleId: receivedVehicleId,
                handledByBackofficeUserId: receivedHandledBy,
              })

              setVehicleId(receivedVehicleId)
              setHandledByBackofficeUserId(receivedHandledBy)
              setPendingUpdate(false)
            }
          },
        }
      : { skip: true },
  )

  return (
    <AdVehicleStreamContext.Provider
      value={{
        vehicleId,
        handledByBackofficeUserId,
        isHandledByCurrentUser,
        pendingUpdate,
        setPendingUpdate,
      }}
    >
      {children}
    </AdVehicleStreamContext.Provider>
  )
}
